import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../../api/Api';

// A small floating "Call staff" control for dine-in guests. Tap → pick an action (call a waiter or
// ask for the bill) → optional table number → notifies the owner's Orders screen (which polls the
// service-request endpoint). Self-contained: manages its own panel, submit and confirmation state.
// Sits bottom-LEFT so it never collides with the cart bar (bottom-centre/right).
const CallStaffButton = ({ venueId, menuId }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [type, setType] = useState('call');   // 'call' = waiter, 'bill' = ask for the bill
    const [table, setTable] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState('');

    const reset = () => { setOpen(false); setDone(false); setTable(''); setType('call'); setErr(''); };

    const send = async () => {
        setBusy(true);
        setErr('');
        try {
            const res = await API.postData('service-request/create', {
                venue_id: venueId,
                menu_id: menuId || 0,
                table_no: table,
                request_type: type,
            });
            if (res.data?.status) {
                setDone(true);
                setTimeout(reset, 2800);
            } else {
                setErr(res.data?.message || t('call_staff.error_notify'));
            }
        } catch (e) {
            setErr(!navigator.onLine
                ? t('call_staff.error_offline')
                : t('call_staff.error_notify'));
        } finally {
            setBusy(false);
        }
    };

    if (!venueId) return null;

    const tab = (key, label) => (
        <button
            type='button'
            onClick={() => setType(key)}
            style={{
                flex: 1, minHeight: 40, borderRadius: 9, cursor: 'pointer',
                border: type === key ? '1px solid var(--mb-accent, #d97757)' : '1px solid #ddd',
                background: type === key ? 'rgba(217, 119, 87,0.10)' : '#fff',
                color: type === key ? '#b8500f' : '#555',
                font: '600 0.82rem system-ui, sans-serif',
            }}>
            {label}
        </button>
    );

    return (
        <div style={{ position: 'fixed', left: 16, bottom: 18, zIndex: 900 }}>
            {open && (
                <div style={{
                    position: 'absolute', bottom: 56, left: 0, width: 244,
                    background: '#fff', borderRadius: 14, padding: '0.9rem',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.18)', border: '1px solid #eee',
                }}>
                    {done ? (
                        <div style={{ font: '600 0.95rem/1.4 system-ui, sans-serif', color: '#16803c' }}>
                            {t('call_staff.done')}
                        </div>
                    ) : (
                        <>
                            <div style={{ font: '600 0.92rem/1.3 system-ui, sans-serif', marginBottom: 8 }}>
                                {t('call_staff.panel_title')}
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                                {tab('call', t('call_staff.tab_call'))}
                                {tab('bill', t('call_staff.tab_bill'))}
                            </div>
                            <label style={{ font: '500 0.78rem/1.2 system-ui, sans-serif', color: '#6b6b60', display: 'block', marginBottom: 4 }}>
                                {t('call_staff.table_label')}
                            </label>
                            <input
                                value={table}
                                onChange={e => setTable(e.target.value)}
                                placeholder={t('call_staff.table_placeholder')}
                                inputMode='text'
                                style={{
                                    width: '100%', padding: '0.5rem 0.6rem', borderRadius: 9,
                                    border: '1px solid #ddd', font: '500 0.9rem system-ui, sans-serif', marginBottom: 8,
                                }}
                            />
                            {err && <div style={{ color: '#b91c1c', font: '500 0.78rem system-ui, sans-serif', marginBottom: 6 }}>{err}</div>}
                            <button
                                onClick={send}
                                disabled={busy}
                                style={{
                                    width: '100%', minHeight: 44, border: 'none', borderRadius: 999,
                                    background: 'var(--mb-accent, #d97757)', color: '#fff',
                                    font: '600 0.9rem system-ui, sans-serif', cursor: 'pointer', opacity: busy ? 0.7 : 1,
                                }}>
                                {busy ? t('call_staff.notify_busy') : (type === 'bill' ? t('call_staff.bill_btn') : t('call_staff.notify_btn'))}
                            </button>
                        </>
                    )}
                </div>
            )}
            <button
                onClick={() => { setOpen(o => !o); setErr(''); }}
                aria-label={t('call_staff.button_label')}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    minHeight: 44, padding: '0 1rem', borderRadius: 999,
                    background: '#fff', color: '#3a352f', border: '1px solid #e3ddd4',
                    font: '600 0.85rem system-ui, sans-serif', boxShadow: '0 4px 14px rgba(0,0,0,0.12)', cursor: 'pointer',
                }}>
                {t('call_staff.button_text')}
            </button>
        </div>
    );
};

export default CallStaffButton;
